/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import QContext from "QContext";
import {QCancelButton, QSaveButton} from "qqq/components/buttons/DefaultButtons";
import RecordQuery from "qqq/pages/records/query/RecordQuery";
import Client from "qqq/utils/qqq/Client";
import FilterUtils from "qqq/utils/qqq/FilterUtils";
import React, {useContext, useEffect, useRef, useState} from "react";

interface RecordPickerModalProps
{
   tableName: string;
   initialQueryFilterJson?: string;
   heading?: string;
   saveButtonLabel?: string;
   saveButtonIcon?: string;

   ///////////////////////////////////////////////////////////////////////////////////////
   // when set, the selection is resolved all the way to record ids before onSelect    //
   // fires (filter-shaped selections get queried inside RecordQuery, join & variant   //
   // aware), capped at this many ids - hosts wanting overflow detection pass max + 1. //
   ///////////////////////////////////////////////////////////////////////////////////////
   resolveToRecordIdsLimit?: number;

   //////////////////////////////////////////////////////////////////////////////////////
   // lets the host reuse the pick-table meta-data this modal loads (e.g., to pass to //
   // a ProcessRun it opens afterward) without fetching it again itself.              //
   //////////////////////////////////////////////////////////////////////////////////////
   onTableMetaDataLoaded?: (tableMetaData: QTableMetaData) => void;

   /////////////////////////////////////////////////////////////////////////////
   // "single" gives the grid radios and allows exactly one row to be chosen. //
   /////////////////////////////////////////////////////////////////////////////
   selectionMode?: "single" | "multiple";

   //////////////////////////////////////////////////////////////////////////////////
   // an already-parsed seed filter, as an alternative to initialQueryFilterJson.  //
   // its leading lockedCriteriaCount criteria render read-only and non-removable. //
   //////////////////////////////////////////////////////////////////////////////////
   initialQueryFilter?: QQueryFilter;
   lockedCriteriaCount?: number;

   onSelect: (selection: string[] | QQueryFilter) => void;
   onClose: () => void;
}

const qController = Client.getInstance();

/*******************************************************************************
 ** Modal that hosts a RecordQuery in its "recordPicker" usage - giving the user
 ** the real query screen (filter + checkboxes) over a table, and handing their
 ** selection (record ids, or a backend-prepped QQueryFilter) back via onSelect.
 *******************************************************************************/
export default function RecordPickerModal({tableName, initialQueryFilterJson, heading, saveButtonLabel, saveButtonIcon, resolveToRecordIdsLimit, onTableMetaDataLoaded, selectionMode, initialQueryFilter: initialQueryFilterProp, lockedCriteriaCount, onSelect, onClose}: RecordPickerModalProps): JSX.Element
{
   const [tableMetaData, setTableMetaData] = useState(null as QTableMetaData);
   const [initialQueryFilter, setInitialQueryFilter] = useState(null as QQueryFilter);
   const [alertContent, setAlertContent] = useState(null as string);
   const [isSaving, setIsSaving] = useState(false);

   const recordQueryRef = useRef<any>();

   const {pushModalOnStack, popModalOffStack} = useContext(QContext);

   /////////////////////////////////////////////////////////////////////////////////////
   // while open, register in QContext's modalStack.  A host RecordQuery behind this  //
   // modal only suppresses its own keyboard shortcuts when the stack is empty        //
   // (RecordQuery's `isModal || modalStack.length == 0`), so without this, moving    //
   // focus to a non-input element in here - clicking a picker grid row, say - leaves //
   // 'n', 'r', 'f' and ',' live behind us.  'n' navigates the whole page to /create, //
   // unmounting this modal and discarding the selection.                             //
   /////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      pushModalOnStack?.("recordPickerModal");
      return () =>
      {
         popModalOffStack?.("recordPickerModal");
      };
   }, []);

   ///////////////////////////////////////////////////////////////////////////////////////
   // load the pick-table's meta-data, and parse the seed filter (if given) - the seed  //
   // json comes from the backend, so clean its values up for frontend use (e.g., that  //
   // possible-value criteria render with labels), as FilterAndColumnsSetupWidget does. //
   //                                                                                   //
   // deps are tableName only - deliberately mount-only with respect to the seed filter //
   // and onTableMetaDataLoaded, since re-parsing a seed filter under an open picker    //
   // would discard whatever the user has since filtered or selected.                   //
   ///////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      let cancelled = false;

      (async () =>
      {
         try
         {
            const tableMetaData = await qController.loadTableMetaData(tableName);

            ///////////////////////////////////////////////////////////////////////////////////////
            // always give RecordQuery an initialQueryFilter - an explicit empty one if we have  //
            // no seed json - since otherwise it falls back to the user's last view of this      //
            // table from local storage, and the picker would open silently pre-filtered by      //
            // whatever they last did on the table's real query screen, hiding pickable records. //
            ///////////////////////////////////////////////////////////////////////////////////////
            ////////////////////////////////////////////////////////////////////////////////
            // note the explicit empty criteria array - QQueryFilter's constructor leaves //
            // criteria undefined when called with no args, and consumers (e.g.           //
            // CustomFilterPanel) then initialize it themselves and push an update, which //
            // changes the filter hash and clears the picker's checkbox selection.        //
            ////////////////////////////////////////////////////////////////////////////////
            let initialQueryFilter: QQueryFilter = new QQueryFilter([]);
            if (initialQueryFilterProp)
            {
               ////////////////////////////////////////////////////////////////////////////////
               // deep copy - cleanupValues... mutates the filter in place, and this one     //
               // belongs to the caller's state (e.g. a DynamicSelect's sourceTable.filter). //
               ////////////////////////////////////////////////////////////////////////////////
               initialQueryFilter = Object.assign(new QQueryFilter(), JSON.parse(JSON.stringify(initialQueryFilterProp)));
               await FilterUtils.cleanupValuesInFilerFromQueryString(qController, tableMetaData, initialQueryFilter);
            }
            else if (initialQueryFilterJson)
            {
               initialQueryFilter = Object.assign(new QQueryFilter(), JSON.parse(initialQueryFilterJson));
               await FilterUtils.cleanupValuesInFilerFromQueryString(qController, tableMetaData, initialQueryFilter);
            }

            //////////////////////////////////////////////////////////////////////////
            // the modal may have been closed while the awaits above were in flight //
            //////////////////////////////////////////////////////////////////////////
            if (cancelled)
            {
               return;
            }

            setInitialQueryFilter(initialQueryFilter);
            setTableMetaData(tableMetaData);
            onTableMetaDataLoaded?.(tableMetaData);
         }
         catch (e: any)
         {
            if (cancelled)
            {
               return;
            }

            console.log(e);
            setAlertContent("Error preparing record picker: " + (e.message ?? "Details not available."));
         }
      })();

      return () =>
      {
         cancelled = true;
      };
   }, [tableName]);


   /*******************************************************************************
    ** close handler for the modal - ignoring backdrop clicks & escape keys, as
    ** other full-screen modals here do.
    *******************************************************************************/
   function handleClose(event?: {}, reason?: "backdropClick" | "escapeKeyDown")
   {
      if (reason == "backdropClick" || reason == "escapeKeyDown")
      {
         return;
      }

      onClose();
   }


   /*******************************************************************************
    ** read the selection out of the RecordQuery and hand it to the host - or, if
    ** nothing is selected, show a message and stay open (cheaper than live-
    ** disabling the button, which would need selection-change plumbing that
    ** RecordQuery doesn't expose).  when the host asked for record ids
    ** (resolveToRecordIdsLimit), resolve the selection through RecordQuery's
    ** join/variant-aware id resolution before handing it over.
    *******************************************************************************/
   async function saveClicked()
   {
      /////////////////////////////////////////////////////////////////////////////////
      // resolving a selection is an await, so without this a second click while the //
      // first is in flight issues a second resolve query and calls onSelect twice - //
      // which, for bulk-add-into-association, adds every picked record twice        //
      /////////////////////////////////////////////////////////////////////////////////
      if (isSaving)
      {
         return;
      }

      const selection: string[] | QQueryFilter = recordQueryRef?.current?.getSelection();

      if (!selection || (Array.isArray(selection) && selection.length == 0))
      {
         setAlertContent(selectionMode == "single" ? "Select a record first." : "Select one or more records first.");
         return;
      }

      if (resolveToRecordIdsLimit != null)
      {
         setIsSaving(true);
         try
         {
            const recordIds: string[] = await recordQueryRef?.current?.getSelectionAsRecordIds(resolveToRecordIdsLimit);
            if (!recordIds || recordIds.length == 0)
            {
               setAlertContent("No records matched the selection.");
               return;
            }
            onSelect(recordIds);
         }
         catch (e: any)
         {
            console.log(e);
            setAlertContent("Error resolving the selection: " + (e.message ?? "Details not available."));
         }
         finally
         {
            setIsSaving(false);
         }
         return;
      }

      onSelect(selection);
   }

   return (
      <Modal open={true} onClose={(event, reason) => handleClose(event, reason)}>
         <div>
            <Box sx={{position: "absolute", overflowY: "auto", maxHeight: "100%", width: "100%"}}>
               <Card sx={{m: "2rem", p: "2rem"}}>
                  <h3>{heading ?? `Select ${tableMetaData?.label ?? ""} Records`}</h3>
                  {
                     alertContent &&
                     <Alert severity="warning" onClose={() => setAlertContent(null)}>{alertContent}</Alert>
                  }
                  {
                     tableMetaData && <RecordQuery
                        ref={recordQueryRef}
                        table={tableMetaData}
                        usage="recordPicker"
                        isModal={true}
                        initialQueryFilter={initialQueryFilter}
                        selectionMode={selectionMode ?? "multiple"}
                        lockedCriteriaCount={lockedCriteriaCount}
                     />
                  }

                  <Box>
                     <Box display="flex" justifyContent="flex-end">
                        <QCancelButton disabled={false} onClickHandler={() => handleClose()} />
                        <QSaveButton label={saveButtonLabel ?? "Add"} iconName={saveButtonIcon ?? "playlist_add"} disabled={tableMetaData == null || isSaving} onClickHandler={saveClicked} />
                     </Box>
                  </Box>
               </Card>
            </Box>
         </div>
      </Modal>
   );
}
